import {
  IntervalItem,
  IntervalWithValueItem,
  LegendConfig,
  LegendType,
  Unit,
} from '@ais-ui/websockets';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@ais-ui/ui-core';
import React, { useEffect, useState } from 'react';
import { cn } from '@ais-ui/utils';
import { PiCaretUpLight, PiSquaresFour } from 'react-icons/pi';
import { useCoverage } from './CoverageContext';

interface LegendProps {
  className: string;
}

interface ItemIntervalProps {
  legend: LegendConfig<LegendType>;
  item: IntervalItem;
}

const getIntervalText = (item: IntervalWithValueItem | IntervalItem) => {
  if (item.start_interval !== item.end_interval) {
    return `${item.start_interval} - ${item.end_interval}`;
  }

  return item.start_interval;
};

const unitToText = (unit: Unit) => {
  switch (unit) {
    case 'count':
      return 'Count';
    case 'km/h':
      return 'Km/h';
    case 'degreesCelsius':
      return '°C';
    case 'percentage':
      return '%';
    case 'knots':
      return 'Knots';
    case 'meters':
      return 'Meters';
    case 'degrees':
      return '°';
    case 'm3':
      return 'm³';
  }
};

const ItemInterval = ({ legend, item }: ItemIntervalProps) => {
  return (
    <div className="flex w-fit min-w-[140px] space-x-4">
      <div
        className="items-center rounded-md border px-2.5 py-0.5 h-6 w-6 border-transparent"
        style={{ backgroundColor: item.color }}
      ></div>
      <div className="w-full flex justify-between space-x-4">
        <div>
          {getIntervalText(item)} {unitToText(legend.unit)}
        </div>
      </div>
    </div>
  );
};

export const CoverageLegend = ({ className }: LegendProps) => {
  const { coverageLegend } = useCoverage();
  const [gridColumnsClass, setGridColumnsClass] = useState('grid-cols-1');
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    const updateGridColumns = () => {
      const maxItemsPerColumn = 15;
      const numberOfColumns = Math.ceil(
        coverageLegend.items.length / maxItemsPerColumn
      );

      setGridColumnsClass(`grid-cols-${numberOfColumns}`);
    };

    updateGridColumns(); // Initial calculation
  }, [coverageLegend.items.length]);

  if (!expanded) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="outline"
            size="icon"
            className={cn('p-1 hover:cursor-pointer', className)}
            onClick={() => setExpanded(true)}
          >
            <PiSquaresFour className="h-6 w-6"></PiSquaresFour>
          </Button>
        </TooltipTrigger>
        <TooltipContent>Expand legend</TooltipContent>
      </Tooltip>
    );
  }

  return (
    <Card className={cn('h-fit group/report-legend relative', className)}>
      <Button
        className="absolute -bottom-3 left-1/2 z-900 bg-background rounded-full transform border border-text-gray-900 text-gray-700 hover:bg-secondary h-6 w-6 p-0 invisible group-hover/report-legend:visible"
        onClick={() => setExpanded(false)}
      >
        <PiCaretUpLight className="h-4 w-4" />
      </Button>
      <CardHeader className="space-y-4">
        <CardTitle className="font-medium text-lg">
          {coverageLegend.title}
        </CardTitle>
      </CardHeader>

      <CardContent>
        <div className={cn('grid gap-y-2 gap-x-6', gridColumnsClass)}>
          {coverageLegend.items.map((item, index) => (
            <ItemInterval key={index} legend={coverageLegend} item={item} />
          ))}
        </div>
      </CardContent>
    </Card>
  );
};
